<template>
    <div class="coach-checkpoint-card">
        <div class="card-header-actions">
            <div class="card-breadcrumbs">
            <ExBreadcrumbs :breadcrumbsList="breadcrumbItems" />
        </div>
        <div class="card-level">
            <CheckpointLevelBand :level="props.level" />
        </div>
        <div class="card-watch-video" v-if="props.video_url">
            <WatchVideoButton class="watch-video-btn" label="Watch Video" @onClick="handleWatchVideo" />
        </div>
        </div>
        <div class="card-header my-3">
            <div class="criteria-title">
                Criteria
            </div>
            <div v-if="props.isInspectionMode" class="card-status">
                <InspectionAction 
                    :view="inspectionView"
                    :isPassed="evaluationFlags.isPassed"
                    :isFailed="evaluationFlags.isFailed"
                    :isNotApplicable="evaluationFlags.isNotApplicable"
                    :isObsolete="evaluationFlags.isObsolete"
                    :failed_reason="props.failed_reason"
                    :not_applicable_reason="props.not_applicable_reason"
                    :obsolete_reason="props.obsolete_reason"
                    :first_name="props.inspector_info.first_name"
                    :last_name="props.inspector_info.last_name"
                    :updated_on="props.inspector_info.updated_on"
                    :updated_by="updatedByObject"
                    :avatar="props.inspector_info.avatar"
                    @onPass="$emit('onPass')"
                    @onFail="$emit('onFail')"
                    @onNotApplicable="$emit('onNotApplicable')"
                    @onObsolete="$emit('onObsolete')"
                    @onClose="$emit('onClose')"
                />
            </div>
        </div>
        <div class="criteria-container">
            <div class="criteria-content">
                {{ props.criteria }}
            </div>
            <div class="criteria-title">
                Instructions
            </div>
        </div>
        <div class="instructions-content" v-html="props.instructions">
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import CheckpointLevelBand from "@components/CheckpointLevelBand/CheckpointLevelBand.vue";
import WatchVideoButton from "@components/WatchVideoButton/WatchVideoButton.vue";
import InspectionAction from "@components/InspectionAction/InspectionAction.vue";

const emit = defineEmits(['onPass', 'onFail', 'onNotApplicable', 'onObsolete', 'onClose', 'watchVideo']);

const props = defineProps({
    criteria: {
        type: String,
        required: true
    },
    instructions: {
        type: String,
        default: ''
    },
    inspection_status: {
        type: String,
        default: '',
        validator: (value) => {
            return ['Not yet evaluated', 'Passed', 'Failed', 'NA', ''].includes(value);
        }
    },
    video_url: {
        type: String,
        default: null
    },
    level: {
        type: String,
        required: true,
        validator: (value) => {
            return ['Apprentice', 'Professional', 'Coach', 'Master'].includes(value);
        }
    },
    failed_reason: {
        type: String,
        default: ''
    },
    not_applicable_reason: {
        type: String,
        default: ''
    },
    obsolete_reason: {
        type: String,
        default: ''
    },
    inspector_info: {
        type: Object,
        default: () => ({
            first_name: '',
            last_name: '',
            updated_on: '',
            avatar: ''
        })
    },
    isInspectionMode: {
        type: Boolean,
        default: true
    }
});

const breadcrumbItems = [
    {
        text: 'Checklist',
        to: '/checklist'
    },
    {
        text: 'Checkpoint',
        disabled: true
    }
];

const inspectionView = computed(() => {
    const statusMap = {
        'Not yet evaluated': 'pending-evaluation',
        'Passed': 'passed-evaluation',
        'Failed': 'failed-evaluation',
        'NA': 'notapplicable-evaluation',
        '': 'pending-evaluation'
    };
    return statusMap[props.inspection_status] || 'pending-evaluation';
});

const evaluationFlags = computed(() => ({
    isPassed: props.inspection_status === 'Passed',
    isFailed: props.inspection_status === 'Failed',
    isNotApplicable: props.inspection_status === 'NA',
    isObsolete: false
}));

const updatedByObject = computed(() => ({
    name: `${props.inspector_info.first_name} ${props.inspector_info.last_name}`.trim(),
    picture: props.inspector_info.avatar
}));

const handleWatchVideo = () => {
    if (props.video_url) {
        emit('watchVideo', props.video_url);
    }
};
</script>

<style scoped>
.coach-checkpoint-card {
    width: 100%;
}

.card-header-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.card-watch-video {
    padding: 4px 0px;
}

.watch-video-btn {
    padding: 0 !important;
}

.watch-video-btn:hover { 
    margin: 0 !important;
}

.criteria-title {
    color: #000;
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px; /* 142.857% */
    padding: 4px 0px;
}

.card-header {
    display: flex;
    justify-content: space-between;
}

.card-status { 
    padding: 0px 22px;
}

.criteria-content {
    color: #000;
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
}

.criteria-container {
    display: flex;
    flex-direction: column;
    gap: 9px;
}
</style>